import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { localeFor, formatTimeFromDate } from '../utils/timeFormat';
import './News.css';

const PER_PAGE = 20;

// Подписи источников не переводим — это названия сайтов
const SOURCE_LABELS = {
  velolive: 'VeloLIVE',
  cyclingnews: 'Cyclingnews'
};

const formatPublishedAt = (value, language) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const isSameYear = date.getFullYear() === new Date().getFullYear();
  const datePart = date.toLocaleDateString(localeFor(language), {
    day: 'numeric',
    month: 'long',
    ...(isSameYear ? {} : { year: 'numeric' })
  });

  return `${datePart}, ${formatTimeFromDate(date, language)}`;
};

// Общий список новостей: на десктопе его показывает модалка, на мобильных — отдельная страница
const NewsList = () => {
  const { t, language } = useLanguage();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);

  const fetchNews = useCallback(async (requestedPage) => {
    const isFirstPage = requestedPage === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setFailed(false);

    try {
      const res = await axios.get('/api/news', {
        params: { language, page: requestedPage, per_page: PER_PAGE }
      });
      const { items: fetched, pagination } = res.data;

      setItems(prev => (isFirstPage ? fetched : [...prev, ...fetched]));
      setPage(pagination.current_page);
      setTotalPages(pagination.total_pages);
    } catch (err) {
      console.error('Failed to fetch news', err);
      setFailed(true);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [language]);

  // Состав источников зависит от языка (velolive только для ru), поэтому при смене перезагружаем
  useEffect(() => {
    fetchNews(1);
  }, [fetchNews]);

  if (loading) {
    return <div className="news-state">{t('news.loading')}</div>;
  }

  if (failed) {
    return (
      <div className="news-state news-state--error">
        <p>{t('news.loadError')}</p>
        <button type="button" className="news-retry" onClick={() => fetchNews(1)}>
          {t('news.retry')}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="news-state">{t('news.empty')}</div>;
  }

  return (
    <div className="news-list">
      {items.map(item => (
        <a
          key={item.id}
          className="news-card"
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.image_url && (
            <div className="news-card-image">
              <img
                src={item.image_url}
                alt=""
                loading="lazy"
                onError={(e) => { e.target.parentNode.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="news-card-body">
            <div className="news-card-meta">
              <span className={`news-source news-source--${item.source}`}>
                {SOURCE_LABELS[item.source] || item.source}
              </span>
              {item.category && <span className="news-category">{item.category}</span>}
              <span className="news-date">{formatPublishedAt(item.published_at, language)}</span>
            </div>
            <h3 className="news-card-title">{item.title}</h3>
            {item.summary && <p className="news-card-summary">{item.summary}</p>}
            {item.author && <div className="news-card-author">{item.author}</div>}
          </div>
        </a>
      ))}

      {page < totalPages && (
        <button
          type="button"
          className="news-load-more"
          onClick={() => fetchNews(page + 1)}
          disabled={loadingMore}
        >
          {loadingMore ? t('news.loading') : t('news.loadMore')}
        </button>
      )}
    </div>
  );
};

export default NewsList;
